import { Head, router } from '@inertiajs/react'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { useState } from 'react'

// Extend dayjs with necessary plugins
dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)

import { useQuery } from '@tanstack/react-query'
import AdminLayout from '@/components/layouts/AdminLayout'
import { PageHeader } from '@/components/patterns'
import { message } from '@/components/ui'
import { EventsTableToolbar } from '@/components/events/EventsTableToolbar'
import { EventsTable } from '@/components/events/EventsTable'
import AssignOwnersModal from '@/components/modals/AssignOwnersModal'
import { api } from '@/lib/apiClient'
import { keys } from '@/lib/queryKeys'
import { Event, Category } from '@/types/events'
import { EventOwner } from '@/pages/admin/event_owners/index.page'
import { useEventFilters } from '@/hooks/useEventFilters'

interface EventReportsProps {
  event_owners?: EventOwner[]
  categories?: Category[]
}

export default function EventReports({ event_owners = [], categories = [] }: EventReportsProps) {
  // Owner assignment modal state
  const [ownerModalVisible, setOwnerModalVisible] = useState<boolean>(false)
  const [currentEvent, setCurrentEvent] = useState<Event | null>(null)
  const [selectedEventOwners, setSelectedEventOwners] = useState<string[]>([])

  // Fetch events data
  const eventsQuery = useQuery({
    queryKey: keys.events.list(),
    queryFn: () => api.get<Event[]>('/admin/events'),
  })

  // Get events data from the query
  const events = eventsQuery.data || []

  // Use event filters hook
  const filters = useEventFilters(events)

  // Handle owner avatar click
  const handleOwnerClick = (event: Event) => {
    setCurrentEvent(event)
    setSelectedEventOwners((event.owner_ids || []).map((id) => id.toString()))
    setOwnerModalVisible(true)
  }

  // Handle assign owners
  const handleAssignOwners = (eventId: string, ownerIds: string[]) => {
    api
      .patch(`/admin/events/${eventId}`, { event: { owner_ids: ownerIds } })
      .then(() => {
        message.success('Event owners updated successfully')
        setSelectedEventOwners(ownerIds)
        eventsQuery.refetch()
        setOwnerModalVisible(false)
      })
      .catch((error) => {
        console.error('Error updating event owners:', error)
        message.error('Failed to update event owners')
      })
  }

  return (
    <AdminLayout>
      <Head title="Event Reports" />
      <div className="w-full bg-[#f5f5f5]">
        <PageHeader
          title="Event Reports"
          description="View report overview for all events."
        />

        <EventsTableToolbar
          searchText={filters.searchText}
          onSearchChange={filters.setSearchText}
          activeTab={filters.activeTab}
          onTabChange={filters.setActiveTab}
          owners={event_owners}
          categories={categories}
          selectedOwners={filters.selectedOwners}
          onOwnersChange={filters.setSelectedOwners}
          selectedCategories={filters.selectedCategories}
          onCategoriesChange={filters.setSelectedCategories}
          selectedDateRange={filters.selectedDateRange}
          onDateRangeChange={filters.setSelectedDateRange}
          ownerFilterVisible={filters.ownerFilterVisible}
          onOwnerFilterVisibleChange={filters.setOwnerFilterVisible}
          categoryFilterVisible={filters.categoryFilterVisible}
          onCategoryFilterVisibleChange={filters.setCategoryFilterVisible}
          dateFilterVisible={filters.dateFilterVisible}
          onDateFilterVisibleChange={filters.setDateFilterVisible}
          onClearFilters={filters.clearFilters}
        />

        <EventsTable
          events={filters.filteredEvents}
          isLoading={eventsQuery.isLoading}
          isError={eventsQuery.isError}
          error={eventsQuery.error as Error}
          viewMode="report"
          categories={categories}
          owners={event_owners}
          onView={(event) => router.visit(`/admin/event_reports/${event.id}`)}
          onOwnerClick={handleOwnerClick}
        />

        <AssignOwnersModal
          visible={ownerModalVisible}
          onCancel={() => setOwnerModalVisible(false)}
          event={currentEvent}
          eventOwners={event_owners}
          initialSelectedOwners={selectedEventOwners}
          onAssign={handleAssignOwners}
        />
      </div>
    </AdminLayout>
  )
}
